import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';

const accountTypes = [
  { type: 'superintendent', label: 'Superintendent', icon: 'fa-user-tie' },
  { type: 'teacher',        label: 'Teacher',         icon: 'fa-chalkboard-teacher' },
  { type: 'student',        label: 'Student',         icon: 'fa-user-graduate' },
  { type: 'accountant',     label: 'Accountant',      icon: 'fa-coins' },
  { type: 'librarian',      label: 'Librarian',       icon: 'fa-book' },
  { type: 'principal',      label: 'Principal',       icon: 'fa-user-graduate' },
  { type: 'receptionist',   label: 'Receptionist',    icon: 'fa-headset' },
  { type: 'lab_attendant',  label: 'Lab Attendant',   icon: 'fa-flask' },
  { type: 'staff',          label: 'Staff',           icon: 'fa-users-cog' },
];

const DESIGNATIONS = ['Office Boy', 'Peon', 'Guard', 'Cleaner', 'Driver', 'Cook', 'Helper', 'Other'];

const emptyChild = () => ({
  first_name: '', last_name: '', registration_number: '',
  date_of_birth: '', gender: 'Male', admission_date: '', class_id: '',
});

export default function AccountCreation() {
  const { user: currentUser } = useAuth();
  const isPrincipal = currentUser?.role === 'principal';
  const isAdminOrSuper = ['admin', 'superintendent'].includes(currentUser?.role);
  const isAccountant = currentUser?.role === 'accountant';

  const [showModal, setShowModal] = useState(false);
  const [selectedType, setSelectedType] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Lookup data
  const [classes, setClasses] = useState([]);
  const [campuses, setCampuses] = useState([]);

  // Parent / general account fields
  const [parent, setParent] = useState({
    email: '', first_name: '', last_name: '', password: '',
    phone: '', address: '', cnic: '',
  });

  // Staff-specific fields
  const [staffForm, setStaffForm] = useState({
    first_name: '', last_name: '', designation: 'Peon',
    phone: '', address: '', cnic: '', join_date: '', salary: 0, campus_id: '',
  });

  // Children (for student creation)
  const [children, setChildren] = useState([emptyChild()]);

  const isStudent = selectedType === 'student';
  const isStaff = selectedType === 'staff';

  // Fetch classes + campuses when the modal opens
  useEffect(() => {
    if (!showModal) return;
    Promise.all([
      api.get('/classes/').catch(() => ({ data: [] })),
      api.get('/campuses/').catch(() => ({ data: [] })),
    ]).then(([c, camp]) => {
      setClasses(c.data || []);
      setCampuses(camp.data || []);
    });
  }, [showModal]);

  const addChild = () => setChildren([...children, emptyChild()]);
  const removeChild = (index) => {
    if (children.length > 1) {
      const next = [...children]; next.splice(index, 1); setChildren(next);
    }
  };
  const updateChild = (index, field, value) => {
    const next = [...children]; next[index][field] = value; setChildren(next);
  };

  const resetForm = () => {
    setParent({ email: '', first_name: '', last_name: '', password: '', phone: '', address: '', cnic: '' });
    setStaffForm({
      first_name: '', last_name: '', designation: 'Peon',
      phone: '', address: '', cnic: '', join_date: '', salary: 0, campus_id: '',
    });
    setChildren([emptyChild()]);
    setError('');
  };

  // Helper: get campus name
  const getCampusName = (id) => campuses.find(c => c.id === id)?.name || '';

  // ─────────────────────────────────────────────────────────
  // MAIN HANDLER
  // ─────────────────────────────────────────────────────────
  const handleCreate = async () => {
    setError('');
    setLoading(true);

    try {
      // ═══════ STAFF CREATION ═══════
      if (isStaff) {
        if (!staffForm.first_name || !staffForm.last_name) {
          setError('First name and last name are required.');
          setLoading(false); return;
        }
        const payload = {
          first_name: staffForm.first_name,
          last_name: staffForm.last_name,
          designation: staffForm.designation,
          phone: staffForm.phone || '',
          address: staffForm.address || '',
          cnic: staffForm.cnic || null,
          join_date: staffForm.join_date || null,
          salary: parseFloat(staffForm.salary) || 0,
          campus_id: parseInt(staffForm.campus_id) || (campuses[0]?.id) || 1,
        };
        await api.post('/staff/', payload);
        alert('✅ Staff created successfully!');
        setShowModal(false); resetForm(); window.location.reload();
        return;
      }

      // ═══════ VALIDATION ═══════
      if (parent.password.length < 8) {
        setError('Password must be at least 8 characters.');
        setLoading(false); return;
      }
      if (!parent.email || !parent.first_name || !parent.last_name) {
        setError('Required fields are missing.');
        setLoading(false); return;
      }
      if (isStudent && !parent.cnic) {
        setError('CNIC is required for parent accounts.');
        setLoading(false); return;
      }

      // ═══════ STUDENT CREATION (Parent + Children) ═══════
      if (isStudent) {
        // Verify every child has a class selected
        const missingClass = children.find(c => c.first_name && c.last_name && !c.class_id);
        if (missingClass) {
          setError(`Please select a class for ${missingClass.first_name} ${missingClass.last_name}.`);
          setLoading(false); return;
        }

        // ─── Find or create parent ───
        const allUsers = await api.get('/users/');
        let existingParent = null;

        if (parent.cnic?.trim()) {
          existingParent = allUsers.data.find(u =>
            u.role === 'parent' && u.cnic && u.cnic === parent.cnic.trim()
          );
        }
        if (!existingParent && parent.email) {
          existingParent = allUsers.data.find(u =>
            u.role === 'parent' && u.email.toLowerCase() === parent.email.toLowerCase()
          );
        }

        let parentId = null;
        if (existingParent) {
          parentId = existingParent.id;
          alert(
            `ℹ️ Parent already exists (${existingParent.first_name} ${existingParent.last_name}).\n` +
            `New student(s) will be linked to this existing parent.`
          );
        } else {
          const parentRes = await api.post('/users/', {
            email: parent.email,
            first_name: parent.first_name,
            last_name: parent.last_name,
            password: parent.password,
            phone: parent.phone || '',
            address: parent.address || '',
            cnic: parent.cnic || null,
            role: 'parent',
          });
          parentId = parentRes.data.id;
        }

        let createdCount = 0;

        for (const child of children) {
          if (!child.first_name || !child.last_name) continue;

          const classObj = classes.find(c => c.id === parseInt(child.class_id));
          if (!classObj) {
            console.warn(`Class not found for ${child.first_name} ${child.last_name}`);
            continue;
          }

          // ─── 1. Create the Student User (login disabled) ───
          const studentEmail = `student_${Date.now()}_${Math.random().toString(36).substring(2, 7)}@school.local`;
          const studentUserRes = await api.post('/users/', {
            email: studentEmail,
            first_name: child.first_name,
            last_name: child.last_name,
            password: 'dummy' + Date.now(),
            phone: '',
            address: parent.address || '',
            cnic: null,
            role: 'student',
            is_active: false,
          });
          const studentUserId = studentUserRes.data.id;

          // ─── 2. Create the Student record ───
          const studentRes = await api.post('/students/', {
            user_id: studentUserId,
            class_id: parseInt(child.class_id),
            registration_number: child.registration_number || '',
            roll_no: null,
            admission_date: child.admission_date || null,
            date_of_birth: child.date_of_birth || null,
            gender: child.gender,
            address: parent.address || '',
            guardian_name: `${parent.first_name} ${parent.last_name}`,
            guardian_phone: parent.phone || '',
            guardian_email: parent.email,
          });
          const studentId = studentRes.data.id;   // 👈 Student table PK

          // ─── 3. ✅ Link parent → student using STUDENT.ID (not User.id) ───
          try {
            await api.post('/parent-students/', {
              parent_id: parentId,
              student_id: studentId,       // 👈 FIXED: was studentUserId
              relationship: 'Parent',
              is_primary: true,
            });
          } catch (linkErr) {
            // 400 = link already exists — ignore
            if (linkErr.response?.status !== 400) throw linkErr;
          }

          createdCount++;
        }

        alert(`✅ ${createdCount} student(s) created and linked successfully!`);
      } else {
        // ═══════ OTHER USER TYPES (teacher, accountant, etc.) ═══════
        await api.post('/users/', {
          email: parent.email,
          first_name: parent.first_name,
          last_name: parent.last_name,
          password: parent.password,
          phone: parent.phone || '',
          address: parent.address || '',
          cnic: parent.cnic || null,
          role: selectedType,
        });
        alert(`✅ ${selectedType} account created successfully!`);
      }

      setShowModal(false);
      resetForm();
      window.location.reload();
    } catch (err) {
      let msg = 'Error creating account.';
      if (err.response?.status === 422) {
        const detail = err.response.data?.detail;
        if (Array.isArray(detail)) msg = detail.map(d => d.msg).join(', ');
        else if (detail) msg = detail;
      } else if (err.response?.data?.detail) {
        msg = err.response.data.detail;
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white p-4 rounded shadow mt-6">
      <h3 className="text-lg font-semibold mb-3">Create Accounts</h3>

      {isPrincipal && (
        <p className="text-sm text-blue-600 bg-blue-50 p-2 rounded mb-3">
          <i className="fas fa-info-circle"></i> Users you create will be assigned to your campus automatically.
        </p>
      )}

      <div className="grid grid-cols-4 gap-4">
        {accountTypes.map((acc) => (
          <div
            key={acc.type}
            className="p-4 border rounded hover:shadow cursor-pointer text-center"
            onClick={() => {
              setSelectedType(acc.type);
              setShowModal(true);
              setError('');
              resetForm();
            }}
          >
            <i className={`fas ${acc.icon} text-2xl text-blue-500`}></i>
            <p className="mt-2 font-medium">{acc.label}</p>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              {isStaff ? '👤 Create Staff Member'
                : isStudent ? '👨‍👩‍👧 Create Student (Parent Account + Children)'
                : `Create ${selectedType}`}
            </h2>

            {isStaff && (
              <p className="text-sm text-blue-600 mb-3 bg-blue-50 p-2 rounded">
                <i className="fas fa-info-circle"></i> Staff cannot log in. The accountant manages their salary.
              </p>
            )}

            {/* ═══════ STAFF FORM ═══════ */}
            {isStaff ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <input className="w-full p-2 border rounded" placeholder="First Name"
                    value={staffForm.first_name}
                    onChange={(e) => setStaffForm({ ...staffForm, first_name: e.target.value })} />
                  <input className="w-full p-2 border rounded" placeholder="Last Name"
                    value={staffForm.last_name}
                    onChange={(e) => setStaffForm({ ...staffForm, last_name: e.target.value })} />
                </div>

                <select className="w-full p-2 border rounded"
                  value={staffForm.designation}
                  onChange={(e) => setStaffForm({ ...staffForm, designation: e.target.value })}>
                  {DESIGNATIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>

                <div className="grid grid-cols-2 gap-3">
                  <input className="w-full p-2 border rounded" placeholder="Phone"
                    value={staffForm.phone}
                    onChange={(e) => setStaffForm({ ...staffForm, phone: e.target.value })} />
                  <input className="w-full p-2 border rounded" placeholder="CNIC / ID Number"
                    value={staffForm.cnic}
                    onChange={(e) => setStaffForm({ ...staffForm, cnic: e.target.value })} />
                </div>

                <input className="w-full p-2 border rounded" placeholder="Address"
                  value={staffForm.address}
                  onChange={(e) => setStaffForm({ ...staffForm, address: e.target.value })} />

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Join Date</label>
                    <input className="w-full p-2 border rounded" type="date"
                      value={staffForm.join_date}
                      onChange={(e) => setStaffForm({ ...staffForm, join_date: e.target.value })} />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Monthly Salary</label>
                    <input className="w-full p-2 border rounded" type="number" placeholder="0"
                      value={staffForm.salary}
                      onChange={(e) => setStaffForm({ ...staffForm, salary: e.target.value })} />
                  </div>
                </div>

                {!isPrincipal && !isAccountant && (
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Campus</label>
                    <select className="w-full p-2 border rounded"
                      value={staffForm.campus_id}
                      onChange={(e) => setStaffForm({ ...staffForm, campus_id: e.target.value })}>
                      <option value="">— Select campus —</option>
                      {campuses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                )}
              </div>
            ) : (
              /* ═══════ PARENT / GENERAL FORM ═══════ */
              <>
                <div className="space-y-4">
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <h4 className="font-bold text-gray-700 mb-3 flex items-center gap-2">
                      <i className="fas fa-user text-blue-500"></i>
                      {isStudent ? '👨 Parent / Guardian Account' : 'Account Details'}
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                      <input className="w-full p-2 border rounded" placeholder="First Name"
                        value={parent.first_name}
                        onChange={(e) => setParent({ ...parent, first_name: e.target.value })} />
                      <input className="w-full p-2 border rounded" placeholder="Last Name"
                        value={parent.last_name}
                        onChange={(e) => setParent({ ...parent, last_name: e.target.value })} />
                      <input className="w-full p-2 border rounded col-span-2" type="email"
                        placeholder="Email (used for login)"
                        value={parent.email}
                        onChange={(e) => setParent({ ...parent, email: e.target.value })} />
                      <input className="w-full p-2 border rounded" type="password"
                        placeholder="Password (min 8 chars)"
                        value={parent.password}
                        onChange={(e) => setParent({ ...parent, password: e.target.value })} />
                      <input className="w-full p-2 border rounded" placeholder="Phone"
                        value={parent.phone}
                        onChange={(e) => setParent({ ...parent, phone: e.target.value })} />
                      <input className="w-full p-2 border rounded col-span-2" placeholder="Address"
                        value={parent.address}
                        onChange={(e) => setParent({ ...parent, address: e.target.value })} />
                      <div className="col-span-2">
                        <input className="w-full p-2 border rounded"
                          placeholder="CNIC / ID Number (e.g., 42101-1234567-8)"
                          value={parent.cnic}
                          onChange={(e) => setParent({ ...parent, cnic: e.target.value })} />
                        {isStudent && (
                          <p className="text-xs text-gray-400 mt-1">
                            💡 Used to identify siblings — same CNIC links to the same parent.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* ═══════ CHILDREN SECTION ═══════ */}
                  {isStudent && (
                    <div>
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="font-bold text-gray-700 flex items-center gap-2">
                          <i className="fas fa-child text-green-500"></i> Children / Students
                        </h4>
                        <button onClick={addChild}
                          className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                          disabled={loading}>
                          + Add Another Child
                        </button>
                      </div>

                      {children.map((child, index) => (
                        <div key={index} className="border rounded-lg p-4 bg-white mb-3">
                          <div className="flex justify-between items-center mb-2">
                            <h5 className="font-semibold text-gray-600">Student #{index + 1}</h5>
                            {children.length > 1 && (
                              <button onClick={() => removeChild(index)}
                                className="text-red-500 hover:text-red-700 text-sm"
                                disabled={loading}>
                                ✕ Remove
                              </button>
                            )}
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <input className="w-full p-2 border rounded" placeholder="Student First Name"
                              value={child.first_name}
                              onChange={(e) => updateChild(index, 'first_name', e.target.value)} />
                            <input className="w-full p-2 border rounded" placeholder="Student Last Name"
                              value={child.last_name}
                              onChange={(e) => updateChild(index, 'last_name', e.target.value)} />
                            <input className="w-full p-2 border rounded" placeholder="Registration Number"
                              value={child.registration_number}
                              onChange={(e) => updateChild(index, 'registration_number', e.target.value)} />

                            <div className="flex items-center gap-2">
                              <label className="text-xs text-gray-500 whitespace-nowrap">📅 DOB:</label>
                              <input className="w-full p-2 border rounded" type="date"
                                value={child.date_of_birth}
                                onChange={(e) => updateChild(index, 'date_of_birth', e.target.value)} />
                            </div>

                            <select className="w-full p-2 border rounded" value={child.gender}
                              onChange={(e) => updateChild(index, 'gender', e.target.value)}>
                              <option value="Male">Male</option>
                              <option value="Female">Female</option>
                              <option value="Other">Other</option>
                            </select>

                            <div className="flex items-center gap-2">
                              <label className="text-xs text-gray-500 whitespace-nowrap">📅 Admission:</label>
                              <input className="w-full p-2 border rounded" type="date"
                                value={child.admission_date}
                                onChange={(e) => updateChild(index, 'admission_date', e.target.value)} />
                            </div>

                            {/* ✅ Class dropdown (instead of text input) */}
                            <div className="col-span-2">
                              <label className="block text-xs text-gray-500 mb-1">
                                Class * <span className="text-gray-400">(required)</span>
                              </label>
                              <select className="w-full p-2 border rounded"
                                value={child.class_id}
                                onChange={(e) => updateChild(index, 'class_id', e.target.value)}>
                                <option value="">— Select a class —</option>
                                {classes.map(c => (
                                  <option key={c.id} value={c.id}>
                                    {c.name}{c.section ? ` · ${c.section}` : ''} — {getCampusName(c.campus_id)}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

            {error && (
              <div className="text-red-500 text-sm mt-3 p-2 bg-red-50 rounded border border-red-200">
                ⚠️ {error}
              </div>
            )}

            <div className="flex justify-end gap-2 mt-4">
              <button className="px-4 py-2 bg-gray-300 rounded"
                onClick={() => { setShowModal(false); setError(''); resetForm(); }}
                disabled={loading}>
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                onClick={handleCreate}
                disabled={loading}>
                {loading ? '⏳ Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}